import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import { COLORS } from '../../utils/constants';
import {
  Settings, Sliders, Bell, Shield, Palette, Layers,
  Sparkles, ArrowLeft, Clock, CheckCircle2, Lock
} from 'lucide-react';

const C = COLORS;

const SettingsPage = () => {
  const navigate = useNavigate();

  const upcomingFeatures = [
    {
      icon: Palette,
      title: 'Theme & Brand Customization',
      desc: 'Set custom color palettes, dark mode preferences, and workspace logos tailored to your brand identity.',
      status: 'In Development',
      color: '#6366F1',
      bg: 'rgba(99, 102, 241, 0.08)',
    },
    {
      icon: Bell,
      title: 'Notification Channels',
      desc: 'Configure transactional emails, push notifications, and webhook alerts for student activities and exam results.',
      status: 'Coming Soon',
      color: '#EC4899',
      bg: 'rgba(236, 72, 153, 0.08)',
    },
    {
      icon: Shield,
      title: 'Security & Access Policies',
      desc: 'Manage session timeouts, two-factor authentication requirements, and IP whitelisting for administrators.',
      status: 'Planned',
      color: '#10B981',
      bg: 'rgba(16, 185, 129, 0.08)',
    },
    {
      icon: Layers,
      title: 'Role & Permission Matrix',
      desc: 'Granular permissions editor for teachers, managers, and editors with custom access tiers across all modules.',
      status: 'In Development',
      color: '#F59E0B',
      bg: 'rgba(245, 158, 11, 0.08)',
    },
  ];

  return (
    <AppLayout title="Settings">
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* Hero Card */}
        <div style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #334155 100%)',
          borderRadius: 24,
          padding: '44px 36px',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.3)',
          marginBottom: 32,
        }}>
          {/* Subtle Glows */}
          <div style={{
            position: 'absolute', top: -50, right: -50, width: 260, height: 260,
            borderRadius: '50%', background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: -50, left: '20%', width: 200, height: 200,
            borderRadius: '50%', background: 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{ maxWidth: 640, position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)', marginBottom: 16 }}>
              <Sparkles size={14} color="#FBBF24" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#FBBF24', letterSpacing: 0.5, textTransform: 'uppercase' }}>Coming Soon</span>
            </div>

            <h1 style={{ margin: '0 0 12px', fontSize: 32, fontWeight: 800, letterSpacing: -0.5 }}>
              System Preferences & Settings
            </h1>

            <p style={{ margin: '0 0 24px', fontSize: 15, color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>
              We're designing a unified management suite to give administrators granular control over workspace preferences, notification channels, security policies, and custom branding.
            </p>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '11px 22px',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(79, 70, 229, 0.4)',
                  transition: 'all 0.2s',
                }}
              >
                <ArrowLeft size={16} />
                Return to Dashboard
              </button>

              <button
                onClick={() => navigate('/profile')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '11px 20px',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s',
                }}
              >
                View Account Profile
              </button>
            </div>
          </div>
        </div>

        {/* Feature Roadmap Grid */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.g900 }}>What's Under Development</h3>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: C.g500 }}>Planned feature modules coming in upcoming updates</p>
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.g400, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={14} /> Release Roadmap 2026
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {upcomingFeatures.map((f, i) => (
              <div
                key={i}
                style={{
                  background: '#fff',
                  borderRadius: 18,
                  border: `1px solid ${C.g200}`,
                  padding: 24,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: f.bg,
                      color: f.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <f.icon size={22} />
                    </div>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: f.bg,
                      color: f.color,
                      letterSpacing: 0.3,
                    }}>
                      {f.status}
                    </span>
                  </div>

                  <h4 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: C.g900 }}>
                    {f.title}
                  </h4>
                  <p style={{ margin: 0, fontSize: 13, color: C.g600, lineHeight: 1.55 }}>
                    {f.desc}
                  </p>
                </div>

                <div style={{ marginTop: 20, paddingTop: 14, borderTop: `1px solid ${C.g100}`, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.g400 }}>
                  <Lock size={12} /> Module locked until deployment
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SettingsPage;
